import { i18n } from 'app/i18n';
import { Line, ProductsSummary, SummaryLine, SummaryPlanLine } from 'app/page/orders/model/products-summary';
import { ChargeType, Overrider, SummaryCharge } from 'app/page/orders/model/summary-charge';
import { TvMapper } from 'app/page/orders/mapper/tv-mapper';
import { TvStaticData } from 'app/page/orders/static/tv-static-data';
import { BroadbandStaticData } from 'app/page/orders/static/broadband-static-data';
import { OptionsStaticData } from 'app/page/orders/static/options-static-data';
import { CurrencyUtils } from 'app/utils/currency-utils';
import { OrderSummary } from 'app/page/orders/model/order-summary';
import { ChargeGroup, Group } from 'app/page/orders/model/charge-group';
import { NumberUtils } from 'app/utils/number-utils';

type ChargesGrouping = Record<string, ChargeGroup[]>;

type ApiCharge =
    | OrderTrackerBackend.OnceOnlyCharge
    | OrderTrackerBackend.RecurringCharge
    | OrderTrackerBackend.InstalmentCharge;

export class OrderSummaryMapper {
    public static mapOrderSummary(apiShoppingCart?: OrderTrackerBackend.ShoppingCart): OrderSummary {
        return {
            initialPrice: OrderSummaryMapper.mapPrice(
                apiShoppingCart &&
                    CurrencyUtils.formatCurrencyValue(
                        parseFloat(apiShoppingCart.summary!.orderPricingSummary!.totalOnceOnlyCharges) +
                            parseFloat(apiShoppingCart.summary!.orderPricingSummary!.totalOnceOnlyDiscounts),
                    ),
            ),
            monthlyPrice: OrderSummaryMapper.mapPrice(
                apiShoppingCart &&
                    CurrencyUtils.formatCurrencyValue(
                        parseFloat(apiShoppingCart.summary!.orderPricingSummary!.totalMonthlyRecurringCharges) +
                            parseFloat(apiShoppingCart.summary!.orderPricingSummary!.totalMonthlyInstalmentCharges),
                    ),
            ),
        };
    }

    private static mapPrice(value?: string): string {
        return `${value || '0'}`;
    }

    public static mapProductsSummary(shoppingCart: OrderTrackerBackend.ShoppingCart): ProductsSummary {
        const charges: SummaryCharge[] = OrderSummaryMapper.toSummaryCharges(
            shoppingCart.summary!.orderPricingSummary!.orderedOffersSummary!.offers,
            [ChargeType.ONCE_ONLY, ChargeType.RECURRING, ChargeType.INSTALMENT],
        );

        const selectedTvChannelOptions: OrderTrackerBackend.Option[] =
            TvMapper.getSelectedTvChannelOptions(shoppingCart);

        const tvChannelPackagesGroups: ChargeGroup[] = TvMapper.getTvChannelPackages(shoppingCart)
            .filter((option) => selectedTvChannelOptions.includes(option))
            .map((channel) => OrderSummaryMapper.mapOptionToChargeGroup(channel, TvStaticData.TV_OFFER_NAME));

        const tvChannelsGroups: ChargeGroup[] = TvMapper.getIndividualTvChannels(shoppingCart)
            .filter((option) => selectedTvChannelOptions.includes(option))
            .map((channel) => OrderSummaryMapper.mapOptionToChargeGroup(channel, TvStaticData.TV_OFFER_NAME));

        const tvStreamingGroups: ChargeGroup[] = TvMapper.getStreamingChannels(shoppingCart).map((channel) => ({
            charge: {
                offerName: TvStaticData.TV_OFFER_NAME,
                itemName: channel.name,
            },
            discount: TvStaticData.TV_STREAMING_DISCOUNTS[channel.name],
        }));

        const grouping: ChargesGrouping = {
            broadbandPlan: [
                {
                    charge: {
                        offerName: BroadbandStaticData.BROADBAND_OFFER_NAME,
                        itemName: BroadbandStaticData.BROADBAND_SERVICE_BANDWIDTH_NAME,
                    },
                },
            ],
            broadband: [
                {
                    discount: {
                        chargeName: BroadbandStaticData.BROADBAND_DISCOUNT_WHEN_BUYING_TV_PACKAGE_CHARGE_NAME,
                    },
                },
                {
                    charge: {
                        chargeName: BroadbandStaticData.BROADBAND_SETUP_CHARGE_NAME,
                    },
                    discount: {
                        chargeName: BroadbandStaticData.BROADBAND_SETUP_DISCOUNT_CHARGE_NAME,
                    },
                },
                {
                    charge: {
                        offerName: BroadbandStaticData.BROADBAND_OFFER_NAME,
                        itemName: OptionsStaticData.CHILI_ANTIVIRUS_CHARACTERISTIC,
                    },
                },
                {
                    charge: {
                        chargeName: OptionsStaticData.PREMIUM_SERVICE_CHARACTERISTIC,
                    },
                },
                {
                    charge: {
                        offerName: BroadbandStaticData.BROADBAND_OFFER_NAME,
                        itemName: OptionsStaticData.CLOUD_CHARACTERISTIC,
                    },
                },
                {
                    charge: {
                        chargeName: BroadbandStaticData.BROADBAND_ACTIVATION_CHARGE_NAME,
                    },
                    discount: {
                        chargeName: BroadbandStaticData.BROADBAND_ACTIVATION_DISCOUNT_CHARGE_NAME,
                    },
                },
            ],
            tvPlan: [
                {
                    charge: {
                        offerName: TvStaticData.TV_OFFER_NAME,
                        productName: TvStaticData.TV_PRODUCT_NAME,
                        itemName: TvStaticData.TV_PACKAGE_NAME,
                    },
                },
            ],
            tv: [
                {
                    charge: {
                        offerName: TvStaticData.TV_OFFER_NAME,
                        productName: TvStaticData.TV_PRODUCT_NAME,
                        itemName: TvStaticData.RECORDING_MODE_NAME,
                    },
                },
            ],
            tvChannelPackages: tvChannelPackagesGroups,
            tvChannels: tvChannelsGroups,
            tvStreaming: tvStreamingGroups,
            equipment: [
                {
                    charge: {
                        offerName: TvStaticData.TV_OFFER_NAME,
                        productName: TvStaticData.TV_PRODUCT_NAME,
                        itemName: OptionsStaticData.TV_BOX_OPTION_NAME,
                    },
                },
                {
                    charge: {
                        offerName: BroadbandStaticData.BROADBAND_OFFER_NAME,
                        itemName: OptionsStaticData.GOOGLE_WIFI_X1_OPTION_NAME,
                    },
                },
                {
                    charge: {
                        offerName: BroadbandStaticData.BROADBAND_OFFER_NAME,
                        itemName: OptionsStaticData.GOOGLE_WIFI_X1_INSTALMENT_OPTION_NAME,
                    },
                },
                {
                    charge: {
                        offerName: TvStaticData.TV_OFFER_NAME,
                        productName: TvStaticData.TV_PRODUCT_NAME,
                        itemName: OptionsStaticData.EXTRA_TV_SETUP_HELP_OPTION_NAME,
                    },
                },
                {
                    charge: {
                        offerName: TvStaticData.TV_OFFER_NAME,
                        productName: TvStaticData.TV_PRODUCT_NAME,
                        itemName: TvStaticData.EXTRA_STB_NAME,
                    },
                },
                // Forsendelse (SHIPPING)
                {
                    charge: {
                        offerName: OptionsStaticData.STANDALONE_RESOURCES_OFFER,
                        productName: undefined,
                        itemName: undefined,
                    },
                },
                {
                    charge: {
                        offerName: OptionsStaticData.GAMING_OFFER,
                        productName: undefined,
                        itemName: OptionsStaticData.GOOGLE_NEST_RESOURCE_NAME,
                    },
                },
                {
                    charge: {
                        offerName: OptionsStaticData.GAMING_OFFER,
                        productName: undefined,
                        itemName: OptionsStaticData.GOOGLE_NEST_ACCESS_POINT_RESOURCE_NAME,
                    },
                },
                // TODO: add additional TV Boxes when available
            ],
        };

        return {
            broadbandPlan: OrderSummaryMapper.toSummaryPlanLines(charges, grouping.broadbandPlan)[0],
            broadband: OrderSummaryMapper.toSummaryLines(charges, grouping.broadband),
            tv: OrderSummaryMapper.toSummaryLines(charges, grouping.tv),
            tvPlan: OrderSummaryMapper.toSummaryPlanLines(charges, grouping.tvPlan)[0],
            tvChannelPackages: OrderSummaryMapper.toSummaryLines(charges, grouping.tvChannelPackages),
            tvChannels: OrderSummaryMapper.toSummaryLines(charges, grouping.tvChannels),
            tvStreaming: OrderSummaryMapper.toSummaryLines(charges, grouping.tvStreaming),
            equipment: OrderSummaryMapper.toSummaryLines(charges, grouping.equipment),
        };
    }

    private static mapOptionToChargeGroup(option: OrderTrackerBackend.Option, offerName: string): ChargeGroup {
        return {
            charge: {
                offerName,
                itemName: option.name,
            },
        };
    }

    private static toSummaryPlanLines(charges: SummaryCharge[], grouping: ChargeGroup[]): SummaryPlanLine[] {
        return grouping
            .map((group) =>
                charges
                    .filter((charge) => OrderSummaryMapper.includeCharge(charge, group.charge))
                    .map(OrderSummaryMapper.mapChargeToSummaryPlanLine),
            )
            .flat();
    }

    private static toSummaryLines(charges: SummaryCharge[], grouping: ChargeGroup[]): SummaryLine[] {
        return grouping
            .map((group) =>
                charges
                    .filter((charge) => OrderSummaryMapper.includeCharge(charge, group.charge || group.discount))
                    .map((charge) =>
                        OrderSummaryMapper.mapChargeToSummaryLine(
                            group.charge && charge,
                            charges
                                .filter((chrg) => OrderSummaryMapper.includeCharge(chrg, group.discount))
                                .find(Boolean),
                        ),
                    )
                    .reduce(OrderSummaryMapper.countQuantity, []),
            )
            .flat();
    }

    private static includeCharge(charge: SummaryCharge, group?: Group): boolean {
        if (!group) {
            return false;
        }

        return (
            (!group.hasOwnProperty('offerName') || charge.offerName === group.offerName) &&
            (!group.hasOwnProperty('productName') || charge.productName === group.productName) &&
            (!group.hasOwnProperty('itemName') || charge.itemName === group.itemName) &&
            (!group.hasOwnProperty('chargeName') || charge.chargeName === group.chargeName)
        );
    }

    private static countQuantity(accumulatedCharges: SummaryLine[], currentCharge: SummaryLine): SummaryLine[] {
        const existingCharge = accumulatedCharges.find(
            (charge) =>
                charge.chargeLine?.title === currentCharge.chargeLine?.title &&
                charge.chargeLine?.priceText === currentCharge.chargeLine?.priceText,
        );

        if (existingCharge?.chargeLine?.quantity) {
            existingCharge.chargeLine.totalPrice += currentCharge.chargeLine?.totalPrice || 0;
            existingCharge.chargeLine.quantity++;
        } else {
            accumulatedCharges.push(currentCharge);
        }

        return accumulatedCharges;
    }

    private static mapChargeToSummaryPlanLine(charge: SummaryCharge): SummaryPlanLine {
        return {
            line: {
                title: charge.title,
                amount: charge.amount,
                priceText: OrderSummaryMapper.getPriceTextByChargeType(charge.chargeType),
                totalPrice: OrderSummaryMapper.getTotalPrice(charge),
            },
            discountLine:
                charge.overrider && OrderSummaryMapper.mapOverriderToLine(charge.overrider, charge.chargeType),
        };
    }

    private static mapChargeToSummaryLine(charge?: SummaryCharge, discount?: SummaryCharge): SummaryLine {
        return {
            chargeLine: charge && OrderSummaryMapper.mapChargeToLine(charge),
            discountLine: discount
                ? OrderSummaryMapper.mapChargeToLine(discount)
                : charge?.overrider && OrderSummaryMapper.mapOverriderToLine(charge.overrider, charge.chargeType),
        };
    }

    private static mapChargeToLine(charge: SummaryCharge): Line {
        return {
            title: charge.title,
            priceText: OrderSummaryMapper.getPriceTextByChargeType(charge.chargeType),
            amount: charge.amount,
            // Extra STB's are different, the quantity is the value of the feature minus 1
            quantity:
                charge.itemName === TvStaticData.EXTRA_STB_NAME && charge.value
                    ? NumberUtils.parseInteger(charge.value) - 1
                    : 1,
            totalPrice: OrderSummaryMapper.getTotalPrice(charge),
        };
    }

    private static mapOverriderToLine(overrider: Overrider, chargeType: ChargeType): Line {
        return {
            title: overrider.title,
            priceText: OrderSummaryMapper.getPriceTextByChargeType(chargeType),
            amount: overrider.amount,
            totalPrice: OrderSummaryMapper.getTotalPrice(overrider),
        };
    }

    private static getTotalPrice({ amount }: { amount: string }): number {
        return parseFloat(amount.replace('.', '').replace(',', '.'));
    }

    private static getPriceTextByChargeType(chargeType: ChargeType): string {
        return i18n.summary[chargeType];
    }

    private static toSummaryCharges(
        offers: OrderTrackerBackend.SummaryOffer[],
        chargeTypes: ChargeType[],
    ): SummaryCharge[] {
        return chargeTypes
            .map((chargeType) => [
                ...offers
                    .map((offer) => [
                        ...(offer[chargeType] as ApiCharge[]).map((charge) =>
                            OrderSummaryMapper.toSummaryCharge(
                                charge,
                                chargeType,
                                undefined,
                                offer.productOffering,
                                undefined,
                                undefined,
                            ),
                        ),
                        ...offer.products
                            .map((product) => [
                                ...(product[chargeType] as ApiCharge[]).map((charge) =>
                                    OrderSummaryMapper.toSummaryCharge(
                                        charge,
                                        chargeType,
                                        undefined,
                                        offer.productOffering,
                                        product.productOffering,
                                        undefined,
                                    ),
                                ),
                                ...product.features
                                    .map((feature) =>
                                        chargeType === ChargeType.INSTALMENT
                                            ? []
                                            : (feature[chargeType] as ApiCharge[]).map((charge) =>
                                                  OrderSummaryMapper.toSummaryCharge(
                                                      charge,
                                                      chargeType,
                                                      feature.value,
                                                      offer.productOffering,
                                                      product.productOffering,
                                                      feature.billText,
                                                  ),
                                              ),
                                    )
                                    .flat(),
                                ...product.resources
                                    .map((resource) =>
                                        (resource[chargeType] as ApiCharge[]).map((charge) =>
                                            OrderSummaryMapper.toSummaryCharge(
                                                charge,
                                                chargeType,
                                                undefined,
                                                offer.productOffering,
                                                product.productOffering,
                                                resource.billText,
                                            ),
                                        ),
                                    )
                                    .flat(),
                            ])
                            .flat(),
                        ...offer.resources
                            .map((resource) =>
                                (resource[chargeType] as ApiCharge[]).map((charge) =>
                                    OrderSummaryMapper.toSummaryCharge(
                                        charge,
                                        chargeType,
                                        undefined,
                                        offer.productOffering,
                                        undefined,
                                        resource.billText,
                                    ),
                                ),
                            )
                            .flat(),
                        ...offer.features
                            .map((feature) =>
                                (feature[chargeType] as ApiCharge[]).map((charge) =>
                                    OrderSummaryMapper.toSummaryCharge(
                                        charge,
                                        chargeType,
                                        feature.value,
                                        offer.productOffering,
                                        undefined,
                                        feature.billText,
                                    ),
                                ),
                            )
                            .flat(),
                    ])
                    .flat(),
            ])
            .flat();
    }

    private static toSummaryCharge(
        charge: ApiCharge,
        chargeType: ChargeType,
        value: string | undefined,
        offerName: string,
        productName: string | undefined,
        itemName: string | undefined,
    ): SummaryCharge {
        switch (chargeType) {
            case ChargeType.INSTALMENT:
                const instalmentCharge: OrderTrackerBackend.InstalmentCharge =
                    charge as OrderTrackerBackend.InstalmentCharge;

                return {
                    title: instalmentCharge.billText,
                    chargeType: ChargeType.INSTALMENT,
                    amount: CurrencyUtils.formatCurrencyValue(+instalmentCharge.amount),
                    numberOfPayments: instalmentCharge.numberOfPayments,
                    frequencyMonths: instalmentCharge.frequencyMonths,
                    chargeName: instalmentCharge.charge,
                    overrider:
                        instalmentCharge.overrider && OrderSummaryMapper.mapOverrider(instalmentCharge.overrider),
                    value,
                    offerName,
                    productName,
                    itemName,
                };
            case ChargeType.ONCE_ONLY:
                const onceOnlyCharge: OrderTrackerBackend.OnceOnlyCharge = charge as OrderTrackerBackend.OnceOnlyCharge;

                return {
                    title: onceOnlyCharge.billText,
                    chargeType: ChargeType.ONCE_ONLY,
                    amount: CurrencyUtils.formatCurrencyValue(+onceOnlyCharge.amount),
                    chargeName: onceOnlyCharge.charge,
                    overrider: onceOnlyCharge.overrider && OrderSummaryMapper.mapOverrider(onceOnlyCharge.overrider),
                    value,
                    offerName,
                    productName,
                    itemName,
                };
            case ChargeType.RECURRING:
                const recurringCharge: OrderTrackerBackend.RecurringCharge =
                    charge as OrderTrackerBackend.RecurringCharge;

                return {
                    title: recurringCharge.billText,
                    chargeType: ChargeType.RECURRING,
                    amount: CurrencyUtils.formatCurrencyValue(+recurringCharge.amount),
                    frequencyMonths: recurringCharge.frequencyMonths,
                    validityPeriodMonths: recurringCharge.validityPeriodMonths,
                    overrider: recurringCharge.overrider && OrderSummaryMapper.mapOverrider(recurringCharge.overrider),
                    chargeName: recurringCharge.charge,
                    value,
                    offerName,
                    productName,
                    itemName,
                };
            default:
                throw new Error(`Not supported charge type: ${chargeType}`);
        }
    }

    private static mapOverrider(overrider: OrderTrackerBackend.Overrider): Overrider {
        return {
            title: overrider.billText,
            amount: overrider.amount && CurrencyUtils.formatCurrencyValue(+overrider.amount),
            chargeName: overrider.charge,
            validityPeriodMonths: overrider.validityPeriodMonths,
            appliedToCharge: overrider.appliedToCharge,
        };
    }
}
